import React, { useState } from 'react';
import { View } from 'react-native';
import { CalcShell, FormulaLine, FormulaNote, NumericField } from '../../../components/shell';
import { DiagramFanTipSpeed } from '../../../components/diagrams';
import { usePersistedState } from '../../../hooks/usePersistedState';
import { useUnits } from '../../../context/UnitsContext';

const KEY = 'tabai.calc.fanTipSpeed.v1';

type Fields = { diameterIn: string; rpm: string; tipSpeed: string };
const EMPTY: Fields = { diameterIn: '', rpm: '', tipSpeed: '' };

/**
 * Fan Tip Speed — TS = π × (D / 12) × RPM (in FPM, Imperial).
 * Bidirectional: fill any 2 of 3 to solve the third.
 */
const FanEquationsTipSpeedScreen = () => {
  const [fields, setFields, { clear: clearPersisted }] = usePersistedState<Fields>(KEY, EMPTY);
  const [error, setError] = useState<string | null>(null);
  const [solvedFor, setSolvedFor] = useState<keyof Fields | null>(null);
  const { unit, toImp, fromImp } = useUnits();

  const set = (patch: Partial<Fields>) => setFields((prev) => ({ ...prev, ...patch }));

  const calculate = () => {
    setError(null);
    const filled = (Object.values(fields) as string[]).filter((v) => v !== '').length;
    if (filled < 2) { setError('Fill any 2 of the 3 fields to solve for the missing one.'); return; }
    if (filled === 3) { setError('All 3 values are filled — clear one to solve for it.'); return; }

    const d = toImp(fields.diameterIn, 'diameter');   // inches
    const r = parseFloat(fields.rpm);                 // RPM (dimensionless unit)
    const ts = toImp(fields.tipSpeed, 'tipSpeed');    // FPM

    if (fields.tipSpeed === '') {
      if (!(d > 0) || !(r > 0)) { setError('Diameter and RPM must be greater than zero.'); return; }
      setSolvedFor('tipSpeed');
      set({ tipSpeed: fromImp(Math.PI * (d / 12) * r, 'tipSpeed', 0) });
    } else if (fields.diameterIn === '') {
      if (!(r > 0)) { setError('RPM must be greater than zero.'); return; }
      const dIn = ts / (Math.PI * r / 12);
      setSolvedFor('diameterIn');
      set({ diameterIn: fromImp(dIn, 'diameter', 2) });
    } else if (fields.rpm === '') {
      if (!(d > 0)) { setError('Diameter must be greater than zero.'); return; }
      const rpm = ts / (Math.PI * (d / 12));
      setSolvedFor('rpm');
      set({ rpm: rpm.toFixed(0) });
    }
  };

  const clearAll = () => {
    setError(null);
    setSolvedFor(null);
    void clearPersisted();
  };

  const result = [
    fields.diameterIn && { label: 'Fan Diameter', value: fields.diameterIn, unit: unit('diameter'), emphasis: solvedFor === 'diameterIn' },
    fields.rpm && { label: 'RPM', value: fields.rpm, unit: 'RPM', emphasis: solvedFor === 'rpm' },
    fields.tipSpeed && { label: 'Tip Speed', value: fields.tipSpeed, unit: unit('tipSpeed'), emphasis: solvedFor === 'tipSpeed' },
  ].filter(Boolean) as { label: string; value: string; unit?: string; emphasis?: boolean }[];

  return (
    <CalcShell
      title="Fan Tip Speed"
      subtitle="Solve for tip speed, diameter, or RPM using TS = π × (D / 12) × RPM"
      formula={
        <>
          <FormulaLine>Tip Speed = π × (D / 12) × RPM</FormulaLine>
          <FormulaNote>D = fan diameter ({unit('diameter')}). Tip speed in {unit('tipSpeed')}.</FormulaNote>
        </>
      }
      error={error}
      onCalculate={calculate}
      onClear={clearAll}
      result={result.length ? result : null}
      copyFooter="Formula: TS = π × (D / 12) × RPM"
      diagram={<DiagramFanTipSpeed />}
      outputSlots={{
        fan_pd_in: fields.diameterIn ? toImp(fields.diameterIn, 'diameter') : undefined,
        fan_rpm: fields.rpm ? parseFloat(fields.rpm) : undefined,
        tip_speed_fpm: fields.tipSpeed ? toImp(fields.tipSpeed, 'tipSpeed') : undefined,
      }}
      onPrefill={(values) => {
        const patch: Partial<Fields> = {};
        if (values.fan_pd_in != null && !fields.diameterIn) patch.diameterIn = fromImp(values.fan_pd_in, 'diameter', 2);
        if (values.fan_rpm != null && !fields.rpm) patch.rpm = values.fan_rpm.toFixed(0);
        if (values.tip_speed_fpm != null && !fields.tipSpeed) patch.tipSpeed = fromImp(values.tip_speed_fpm, 'tipSpeed', 0);
        if (Object.keys(patch).length > 0) set(patch);
      }}
    >
      <View style={{ marginBottom: 12 }}>
        <NumericField label={`Fan Diameter (${unit('diameter')})`} value={fields.diameterIn} onChangeText={(v) => set({ diameterIn: v })} rightText={unit('diameter')} onSubmitEditing={calculate} />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField label="RPM" value={fields.rpm} onChangeText={(v) => set({ rpm: v })} rightText="RPM" onSubmitEditing={calculate} />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField label={`Tip Speed (${unit('tipSpeed')})`} value={fields.tipSpeed} onChangeText={(v) => set({ tipSpeed: v })} rightText={unit('tipSpeed')} onSubmitEditing={calculate} />
      </View>
    </CalcShell>
  );
};

export default FanEquationsTipSpeedScreen;
